// IOS commands for infrastructure services (Domain 05): DHCP server extras,
// static / dynamic NAT and PAT pools, HSRP / VRRP / GLBP, NTP, Syslog and SNMP.
// Same spec shape as ./cli.js: { pattern, kinds, run(s, args) }.

import { ifStatus, l3Addr } from '../net/sim.js';
import { maskToPrefix, networkOf, ipToInt, isIp } from '../net/addr.js';
import { fhrpState, ntpState, clockLine, logEvent, SEVERITY, LAB_NOW } from '../net/services.js';
import { shortIf, ifSort } from './ifnames.js';

const R = (specs) => specs.map((x) => ({ ...x, kinds: ['router'] }));
const pad = (s, n) => String(s).padEnd(n);
const lpad = (s, n) => String(s).padStart(n);
const INVALID = ["% Invalid input detected at '^' marker."];
const curIf = (s) => s.d.interfaces[s.state.ifs[0]];
const one = (fn) => (s, args) => (s.state.ifs.length === 1 ? fn(s, args) : INVALID);

// ------------------------------------------------------------------ DHCP (pool sub-mode additions + global)
export const DHCP_POOL_EXTRA = R([
  { pattern: 'domain-name <WORD>', run: (s, [n]) => { s.pool().domain = n.toLowerCase(); return []; } },
  { pattern: 'no domain-name', run: (s) => { delete s.pool().domain; return []; } },
  { pattern: 'no default-router', run: (s) => { delete s.pool().router; return []; } },
  { pattern: 'no dns-server', run: (s) => { s.pool().dns = []; return []; } },
  { pattern: 'no network', run: (s) => { const p = s.pool(); delete p.network; delete p.mask; return []; } },
  { pattern: 'lease <0-365>', run: (s, [d]) => { s.pool().leaseDays = +d; return []; } },
]);
const sameRange = (e, a, b) => e[0] === a && (e[1] || null) === (b || null);
export const SVC_CONFIG = R([
  { pattern: 'no ip dhcp excluded-address <A.B.C.D>', run: (s, [a]) => { s.d.dhcp.excluded = s.d.dhcp.excluded.filter((e) => !sameRange(e, a)); return []; } },
  { pattern: 'no ip dhcp excluded-address <A.B.C.D> <A.B.C.D>', run: (s, [a, b]) => { s.d.dhcp.excluded = s.d.dhcp.excluded.filter((e) => !sameRange(e, a, b)); return []; } },
  // NAT
  { pattern: 'ip nat inside source static <A.B.C.D> <A.B.C.D>', run: (s, [l, g]) => natStatic(s, l, g, true) },
  { pattern: 'no ip nat inside source static <A.B.C.D> <A.B.C.D>', run: (s, [l, g]) => natStatic(s, l, g, false) },
  { pattern: 'ip nat pool <WORD> <A.B.C.D> <A.B.C.D> netmask <A.B.C.D>', run: (s, [n, a, b, m]) => natPool(s, n, a, b, m) },
  { pattern: 'no ip nat pool <WORD>', run: (s, [n]) => { if (s.d.nat.rules.some((r) => r.pool === n)) return [`%Pool ${n} in use, cannot destroy`]; if (s.d.nat.pools) delete s.d.nat.pools[n]; return []; } },
  { pattern: 'ip nat inside source list <1-99> pool <WORD>', run: (s, [a, p]) => natPoolRule(s, a, p, false, true) },
  { pattern: 'ip nat inside source list <1-99> pool <WORD> overload', run: (s, [a, p]) => natPoolRule(s, a, p, true, true) },
  { pattern: 'no ip nat inside source list <1-99> pool <WORD>', run: (s, [a, p]) => natPoolRule(s, a, p, false, false) },
  { pattern: 'no ip nat inside source list <1-99> pool <WORD> overload', run: (s, [a, p]) => natPoolRule(s, a, p, true, false) },
  // NTP
  { pattern: 'ntp server <A.B.C.D>', run: (s, [ip]) => { const n = (s.d.ntp ||= { servers: [], master: null }); if (!n.servers.includes(ip)) n.servers.push(ip); return []; } },
  { pattern: 'no ntp server <A.B.C.D>', run: (s, [ip]) => { if (s.d.ntp) s.d.ntp.servers = s.d.ntp.servers.filter((x) => x !== ip); return []; } },
  { pattern: 'ntp master', run: (s) => { (s.d.ntp ||= { servers: [], master: null }).master = 8; return []; } },
  { pattern: 'ntp master <1-15>', run: (s, [n]) => { (s.d.ntp ||= { servers: [], master: null }).master = +n; return []; } },
  { pattern: 'no ntp master', run: (s) => { if (s.d.ntp) s.d.ntp.master = null; return []; } },
  // Syslog
  { pattern: 'logging host <A.B.C.D>', run: (s, [ip]) => logHost(s, ip, true) },
  { pattern: 'logging <A.B.C.D>', run: (s, [ip]) => logHost(s, ip, true) },
  { pattern: 'no logging host <A.B.C.D>', run: (s, [ip]) => logHost(s, ip, false) },
  { pattern: 'no logging <A.B.C.D>', run: (s, [ip]) => logHost(s, ip, false) },
  { pattern: 'logging trap <WORD>', run: (s, [l]) => logTrap(s, l) },
  { pattern: 'no logging trap', run: (s) => { (s.d.logging ||= { hosts: [] }).trap = 'informational'; return []; } },
  { pattern: 'logging buffered', run: (s) => { (s.d.logging ||= { hosts: [] }).buffered = true; return []; } },
  { pattern: 'logging on', run: () => [] },
  { pattern: 'service timestamps log datetime msec', run: (s) => { s.d.timestamps = true; return []; } },
  // SNMP
  { pattern: 'snmp-server community <WORD> ro', run: (s, [c]) => snmpComm(s, c, 'ro') },
  { pattern: 'snmp-server community <WORD> rw', run: (s, [c]) => snmpComm(s, c, 'rw') },
  { pattern: 'snmp-server community <WORD>', run: (s, [c]) => snmpComm(s, c, 'ro') },
  { pattern: 'no snmp-server community <WORD>', run: (s, [c]) => { if (s.d.snmp) delete s.d.snmp.communities[c]; return []; } },
  { pattern: 'snmp-server host <A.B.C.D> version 2c <WORD>', run: (s, [ip, c]) => snmpHost(s, ip, c) },
  { pattern: 'snmp-server host <A.B.C.D> <WORD>', run: (s, [ip, c]) => snmpHost(s, ip, c) },
  { pattern: 'no snmp-server host <A.B.C.D>', run: (s, [ip]) => { if (s.d.snmp) s.d.snmp.hosts = s.d.snmp.hosts.filter((h) => h.ip !== ip); return []; } },
  { pattern: 'snmp-server enable traps', run: (s) => { snmp(s.d).traps = true; return []; } },
  { pattern: 'no snmp-server enable traps', run: (s) => { snmp(s.d).traps = false; return []; } },
  { pattern: 'snmp-server location <LINE>', run: (s, [t]) => { snmp(s.d).location = t; return []; } },
  { pattern: 'snmp-server contact <LINE>', run: (s, [t]) => { snmp(s.d).contact = t; return []; } },
]);
const snmp = (d) => (d.snmp ||= { communities: {}, hosts: [], traps: false });
function snmpComm(s, c, mode) { snmp(s.d).communities[c] = mode; return []; }
function snmpHost(s, ip, c) { const S = snmp(s.d); S.hosts = S.hosts.filter((h) => h.ip !== ip); S.hosts.push({ ip, version: '2c', community: c }); return []; }
function logHost(s, ip, add) {
  const L = (s.d.logging ||= { hosts: [], trap: 'informational' });
  L.hosts = L.hosts.filter((x) => x !== ip);
  if (add) L.hosts.push(ip);
  return [];
}
function logTrap(s, l) {
  const v = /^\d$/.test(l) ? SEVERITY[+l] : SEVERITY.find((x) => x.startsWith(l.toLowerCase()));
  if (!v) return INVALID;
  (s.d.logging ||= { hosts: [] }).trap = v;
  return [];
}
function natStatic(s, l, g, add) {
  const N = s.d.nat;
  N.statics = (N.statics || []).filter((x) => !(x.local === l && x.global === g));
  if (add) {
    if (N.statics.some((x) => x.local === l)) return [`% ${l} already mapped (${l} -> ${N.statics.find((x) => x.local === l).global})`];
    if (N.statics.some((x) => x.global === g)) return [`% similar static entry (${N.statics.find((x) => x.global === g).local} -> ${g}) already exists`];
    N.statics.push({ local: l, global: g });
  }
  return [];
}
function natPool(s, n, a, b, m) {
  if (maskToPrefix(m) === null) return ['%Invalid netmask'];
  if (networkOf(a, m) !== networkOf(b, m)) return ['%End address not in the same network as the start address'];
  if ((ipToInt(b) >>> 0) < (ipToInt(a) >>> 0)) return ['%End address less than start address'];
  (s.d.nat.pools ||= {})[n] = { start: a, end: b, mask: m };
  return [];
}
function natPoolRule(s, acl, pool, overload, add) {
  const N = s.d.nat;
  N.rules = N.rules.filter((r) => !(r.acl === acl && r.pool === pool));
  if (add) {
    if (!N.pools?.[pool]) return [`%Pool ${pool} not found`];
    N.rules.push({ acl, pool, overload });
  }
  return [];
}

// ------------------------------------------------------------------ interface: FHRP
const group = (s, proto, g) => {
  const i = curIf(s);
  i.fhrp = Array.isArray(i.fhrp) ? i.fhrp : [];
  let x = i.fhrp.find((e) => e.proto === proto && String(e.group) === String(g));
  if (!x) { x = { proto, group: +g, vip: null, priority: 100, preempt: proto !== 'hsrp' ? true : false }; i.fhrp.push(x); }
  return x;
};
const dropGroup = (s, proto, g) => { const i = curIf(s); i.fhrp = (i.fhrp || []).filter((e) => !(e.proto === proto && String(e.group) === String(g))); return []; };
const vipOk = (s, ip) => {
  const a = curIf(s).ip;
  if (!a) return '% Address  cannot be configured: interface has no IP address';
  if (networkOf(ip, a.mask) !== networkOf(a.ip, a.mask)) return `% Address ${ip} in group is not in the interface subnet`;
  return null;
};
export const SVC_IF = R([
  ...[['standby', 'hsrp', '<0-255>'], ['vrrp', 'vrrp', '<1-255>'], ['glbp', 'glbp', '<0-1023>']].flatMap(([kw, proto, rng]) => [
    { pattern: `${kw} ${rng} ip <A.B.C.D>`, run: one((s, [g, ip]) => { const e = vipOk(s, ip); if (e && !(proto === 'vrrp' && curIf(s).ip?.ip === ip)) return [e]; group(s, proto, g).vip = ip; return []; }) },
    { pattern: `${kw} ${rng} priority <0-255>`, run: one((s, [g, p]) => { group(s, proto, g).priority = +p; return []; }) },
    { pattern: `${kw} ${rng} preempt`, run: one((s, [g]) => { group(s, proto, g).preempt = true; return []; }) },
    { pattern: `no ${kw} ${rng} preempt`, run: one((s, [g]) => { group(s, proto, g).preempt = false; return []; }) },
    { pattern: `no ${kw} ${rng} priority`, run: one((s, [g]) => { group(s, proto, g).priority = 100; return []; }) },
    { pattern: `no ${kw} ${rng}`, run: one((s, [g]) => dropGroup(s, proto, g)) },
    { pattern: `no ${kw} ${rng} ip`, run: one((s, [g]) => { group(s, proto, g).vip = null; return []; }) },
  ]),
  { pattern: 'no ip helper-address', run: (s) => { for (const n of s.state.ifs) delete s.d.interfaces[n].helper; return []; } },
  { pattern: 'no ip helper-address <A.B.C.D>', run: (s, [ip]) => { for (const n of s.state.ifs) if (s.d.interfaces[n].helper === ip) delete s.d.interfaces[n].helper; return []; } },
  { pattern: 'standby version <1-2>', run: one((s, [v]) => { curIf(s).hsrpVersion = +v; return []; }) },
  { pattern: 'glbp <0-1023> load-balancing round-robin', run: one((s, [g]) => { group(s, 'glbp', g).lb = 'round-robin'; return []; }) },
  { pattern: 'glbp <0-1023> load-balancing host-dependent', run: one((s, [g]) => { group(s, 'glbp', g).lb = 'host-dependent'; return []; }) },
  { pattern: 'glbp <0-1023> load-balancing weighted', run: one((s, [g]) => { group(s, 'glbp', g).lb = 'weighted'; return []; }) },
]);

// ------------------------------------------------------------------ show
const fhrpRows = (net, id, proto) => Object.values(fhrpState(net)).filter((x) => x.dev === id && x.proto === proto).sort((a, b) => ifSort(a.ifc, b.ifc));
const vmac = (proto, g, n = 1) => (proto === 'hsrp' ? `0000.0c07.ac${g.toString(16).padStart(2, '0')}` : proto === 'vrrp' ? `0000.5e00.01${g.toString(16).padStart(2, '0')}` : `0007.b400.${g.toString(16).padStart(2, '0')}${n.toString(16).padStart(2, '0')}`);
function standbyBrief(net, id) {
  const out = ['                     P indicates configured to preempt.', '                     |', 'Interface   Grp  Pri P State   Active          Standby         Virtual IP'];
  for (const x of fhrpRows(net, id, 'hsrp')) {
    const act = x.state === 'Active' ? 'local' : x.active?.ip || 'unknown';
    const sby = x.state === 'Standby' ? 'local' : x.standby?.ip || 'unknown';
    out.push(`${pad(shortIf(x.ifc), 12)}${pad(x.group, 5)}${lpad(x.priority, 3)} ${x.preempt ? 'P' : ' '} ${pad(x.state, 8)}${pad(act, 16)}${pad(sby, 16)}${x.configuredVip || x.vip || 'unknown'}`);
  }
  return out;
}
function standbyDetail(net, id) {
  const out = [];
  for (const x of fhrpRows(net, id, 'hsrp')) {
    out.push(`${x.ifc} - Group ${x.group}`, `  State is ${x.state}`, `    2 state changes, last state change 00:04:12`, `  Virtual IP address is ${x.configuredVip || x.vip || 'unknown'}${x.vipConflict ? ' (conflicts with the Active router)' : ''}`,
      `  Active virtual MAC address is ${vmac('hsrp', x.group)} (MAC ${x.state === 'Active' ? 'In Use' : 'Not In Use'})`, '    Local virtual MAC address is ' + vmac('hsrp', x.group) + ' (v1 default)',
      '  Hello time 3 sec, hold time 10 sec', `  Preemption ${x.preempt ? 'enabled' : 'disabled'}`,
      `  Active router is ${x.state === 'Active' ? 'local' : x.active ? `${x.active.ip}, priority ${x.active.priority}` : 'unknown'}`,
      `  Standby router is ${x.state === 'Standby' ? 'local' : x.standby ? `${x.standby.ip}, priority ${x.standby.priority}` : 'unknown'}`,
      `  Priority ${x.priority} (${x.priority === 100 ? 'default 100' : 'configured ' + x.priority})`, `  Group name is "hsrp-${shortIf(x.ifc)}-${x.group}" (default)`, '');
  }
  return out;
}
function vrrpBrief(net, id) {
  const out = ['Interface          Grp Pri Time  Own Pre State   Master addr     Group addr'];
  for (const x of fhrpRows(net, id, 'vrrp')) out.push(`${pad(shortIf(x.ifc), 19)}${pad(x.group, 4)}${pad(x.priority, 4)}${pad(3609, 6)}${pad(x.priority === 255 ? 'Y' : ' ', 4)}${pad(x.preempt ? 'Y' : 'N', 4)}${pad(x.state, 8)}${pad(x.active?.ip || 'unknown', 16)}${x.configuredVip || x.vip || ''}`);
  return out;
}
function vrrpDetail(net, id) {
  const out = [];
  for (const x of fhrpRows(net, id, 'vrrp')) out.push(`${x.ifc} - Group ${x.group}`, `  State is ${x.state}`, `  Virtual IP address is ${x.configuredVip || x.vip}`, `  Virtual MAC address is ${vmac('vrrp', x.group)}`, '  Advertisement interval is 1.000 sec', `  Preemption ${x.preempt ? 'enabled' : 'disabled'}`, `  Priority is ${x.priority}`, `  Master Router is ${x.state === 'Master' ? `${x.ip} (local)` : x.active?.ip || 'unknown'}, priority is ${x.active?.priority ?? '-'}`, '  Master Advertisement interval is 1.000 sec', '  Master Down interval is 3.609 sec', '');
  return out;
}
function glbpBrief(net, id) {
  const out = ['Interface   Grp  Fwd Pri State    Address         Active router   Standby router'];
  for (const x of fhrpRows(net, id, 'glbp')) {
    const avgState = x.avg?.dev === id ? 'Active' : 'Standby';
    out.push(`${pad(shortIf(x.ifc), 12)}${pad(x.group, 5)}${pad('-', 4)}${pad(x.priority, 4)}${pad(avgState, 9)}${pad(x.vip || '', 16)}${pad(x.avg?.dev === id ? 'local' : x.avg?.ip || 'unknown', 16)}${x.standby?.dev === id ? 'local' : x.standby?.ip || 'unknown'}`);
    for (const f of x.forwarders || []) out.push(`${pad(shortIf(x.ifc), 12)}${pad(x.group, 5)}${pad(f.n, 4)}${pad('-', 4)}${pad(f.dev === id ? 'Active' : 'Listen', 9)}${pad(vmac('glbp', x.group, f.n), 16)}${pad(f.dev === id ? 'local' : f.ip, 16)}${f.dev === id ? '-' : 'local'}`);
  }
  return out;
}
function glbpDetail(net, id) {
  const out = [];
  for (const x of fhrpRows(net, id, 'glbp')) {
    out.push(`${x.ifc} - Group ${x.group}`, `  State is ${x.avg?.dev === id ? 'Active' : 'Standby'}`, `  Virtual IP address is ${x.vip}`, '  Hello time 3 sec, hold time 10 sec', `  Preemption ${x.preempt ? 'enabled' : 'disabled'}, min delay 0 sec`,
      `  Active is ${x.avg?.dev === id ? 'local' : x.avg?.ip}, priority ${x.avg ? '' : ''}`.trim(), `  Priority ${x.priority} (${x.priority === 100 ? 'default' : 'configured'})`, '  Weighting 100 (default 100), thresholds: lower 1, upper 100',
      `  Load balancing: ${(net.devices[id].interfaces[x.ifc].fhrp || []).find((g) => g.proto === 'glbp')?.lb || 'round-robin'}`, `  Group members:`);
    for (const f of x.forwarders || []) out.push(`    ${net.devices[f.dev].interfaces[f.ifc].mac ? net.devices[f.dev].interfaces[f.ifc].mac.match(/.{4}/g).join('.') : ''} (${f.ip})${f.dev === id ? ' local' : ''}`);
    out.push(`  There are ${(x.forwarders || []).length} forwarders (${(x.forwarders || []).length} active)`);
    for (const f of x.forwarders || []) out.push(`  Forwarder ${f.n}`, `    State is ${f.dev === id ? 'Active' : 'Listen'}`, `    MAC address is ${vmac('glbp', x.group, f.n)} (default)`, `    Owner ID is ${f.ip}`);
    out.push('');
  }
  return out;
}
function ntpAssoc(net, id) {
  const d = net.devices[id];
  const st = ntpState(net)[id];
  const out = ['  address         ref clock       st   when   poll reach  delay  offset   disp'];
  for (const ip of d.ntp?.servers || []) {
    const sel = st?.server === ip;
    const reach = st?.reachable?.[ip];
    const owner = Object.entries(net.devices).find(([oid, o]) => (o.kind === 'router' ? Object.keys(o.interfaces).some((n) => l3Addr(net, oid, n)?.ip === ip) : o.ipcfg?.ip === ip));
    const ost = owner ? ntpState(net)[owner[0]] : null;
    const ref = reach && ost?.synced ? (ost.refId || '.GPS.') : '.INIT.';
    const strat = reach && ost?.synced ? ost.stratum : 16;
    out.push(`${sel ? '*' : reach ? '+' : ' '}~${pad(ip, 15)}${pad(ref, 16)}${lpad(strat, 2)}${lpad(reach ? 31 : '-', 7)}${lpad(64, 7)}${lpad(reach ? 377 : 0, 6)}${lpad(reach ? '1.204' : '0.000', 7)}${lpad(reach ? '-0.318' : '0.000', 8)}${lpad(reach ? '1.017' : '15937.', 8)}`);
  }
  if (d.ntp?.master) out.push(`*~127.127.1.1    .LOCL.          ${lpad(d.ntp.master - 1, 2)}      7     16   377  0.000   0.000  0.236`);
  out.push(' * sys.peer, # selected, + candidate, - outlyer, x falseticker, ~ configured');
  return out;
}
function ntpStatus(net, id) {
  const st = ntpState(net)[id] || { synced: false, stratum: 16 };
  if (!st.synced) return ['Clock is unsynchronized, stratum 16, no reference clock', 'nominal freq is 250.0000 Hz, actual freq is 250.0000 Hz, precision is 2**10', 'ntp uptime is 0 (1/100 of seconds), resolution is 4000', 'reference time is 00000000.00000000 (00:00:00.000 UTC Mon Jan 1 1900)', 'clock offset is 0.0000 msec, root delay is 0.00 msec', 'root dispersion is 0.00 msec, peer dispersion is 0.00 msec', 'loopfilter state is \'NSET\' (Never set), drift is 0.000000000 s/s', 'system poll interval is 64, never updated.'];
  return [`Clock is synchronized, stratum ${st.stratum}, reference is ${st.server || '127.127.1.1'}`, 'nominal freq is 250.0000 Hz, actual freq is 250.0000 Hz, precision is 2**10', 'ntp uptime is 1532400 (1/100 of seconds), resolution is 4000',
    `reference time is ${(Math.floor(LAB_NOW / 1000) + 2208988800).toString(16).toUpperCase()}.3A1CAC08 (01:14:07.221 UTC Wed Oct 7 2026)`, 'clock offset is -0.3180 msec, root delay is 1.20 msec', 'root dispersion is 2.71 msec, peer dispersion is 1.01 msec', 'loopfilter state is \'CTRL\' (Normal Controlled Loop), drift is 0.000001245 s/s', 'system poll interval is 64, last update was 31 sec ago.'];
}
function showLogging(net, id) {
  const d = net.devices[id];
  const L = d.logging || { hosts: [], trap: 'informational' };
  const logs = d.logs || [];
  const trapLvl = L.trap || 'informational';
  const out = ['Syslog logging: enabled (0 messages dropped, 0 messages rate-limited, 0 flushes, 0 overruns, xml disabled, filtering disabled)', '',
    'No Active Message Discriminator.', '', '', 'No Inactive Message Discriminator.', '', '',
    '    Console logging: level debugging, ' + (logs.length + 12) + ' messages logged, xml disabled,', '                     filtering disabled',
    '    Monitor logging: level debugging, 0 messages logged, xml disabled,', '                     filtering disabled',
    `    Buffer logging:  ${L.buffered === false ? 'disabled' : 'level debugging, ' + logs.length + ' messages logged, xml disabled,'}`, '                    filtering disabled',
    '    Exception Logging: size (4096 bytes)', '    Count and timestamp logging messages: disabled', '    Persistent logging: disabled', '',
    'No active filter modules.', '',
    `    Trap logging: level ${trapLvl}, ${logs.filter((e) => e.sev <= SEVERITY.indexOf(trapLvl)).length} message lines logged`];
  for (const h of L.hosts || []) out.push(`        Logging to ${h}  (udp port 514, audit disabled,`, '              link up),', `              ${logs.filter((e) => e.sev <= SEVERITY.indexOf(trapLvl)).length} message lines logged,`, '              0 message lines rate-limited,', '              0 message lines dropped-by-MD,', '              xml disabled, sequence number disabled', '              filtering disabled');
  out.push('        Logging Source-Interface:       VRF Name:', '', `Log Buffer (8192 bytes):`, '');
  for (const e of logs) out.push(`${e.t}: ${e.text}`);
  return out;
}
function showSnmp(d) {
  const S = d.snmp;
  if (!S || !Object.keys(S.communities || {}).length) return ['%SNMP agent not enabled'];
  return [`Chassis: FTX1840A${(d.id.charCodeAt(0) * 37) % 1000}`, ...(S.contact ? [`Contact: ${S.contact}`] : []), ...(S.location ? [`Location: ${S.location}`] : []),
    '1342 SNMP packets input', '    0 Bad SNMP version errors', `    ${S.badCommunity || 0} Unknown community name`, '    0 Illegal operation for community name supplied', '    0 Encoding errors', '    1342 Number of requested variables',
    '1342 SNMP packets output', '    0 Too big errors (Maximum packet size 1500)', '    0 No such name errors', '    0 Bad values errors', '    0 General errors', '    1342 Response PDUs', `    ${S.traps ? 6 : 0} Trap PDUs`, '',
    `SNMP logging: ${S.hosts?.length ? 'enabled' : 'disabled'}`, ...(S.hosts || []).map((h) => `    Logging to ${h.ip}.162, 0/10, ${S.traps ? 6 : 0} sent, 0 dropped.`)];
}
export const SVC_SHOW = R([
  { pattern: 'show standby brief', run: (s) => standbyBrief(s.net, s.id) },
  { pattern: 'show standby', run: (s) => standbyDetail(s.net, s.id) },
  { pattern: 'show vrrp brief', run: (s) => vrrpBrief(s.net, s.id) },
  { pattern: 'show vrrp', run: (s) => vrrpDetail(s.net, s.id) },
  { pattern: 'show glbp brief', run: (s) => glbpBrief(s.net, s.id) },
  { pattern: 'show glbp', run: (s) => glbpDetail(s.net, s.id) },
  { pattern: 'show ntp associations', run: (s) => ntpAssoc(s.net, s.id) },
  { pattern: 'show ntp status', run: (s) => ntpStatus(s.net, s.id) },
  { pattern: 'show logging', run: (s) => showLogging(s.net, s.id) },
  { pattern: 'show snmp', run: (s) => showSnmp(s.d) },
  { pattern: 'show snmp community', run: (s) => Object.entries(s.d.snmp?.communities || {}).flatMap(([c, m]) => [`Community name: ${c}`, 'Community Index: ' + c, 'Community SecurityName: ' + c, `storage-type: nonvolatile        active   access: ${m === 'rw' ? 'read-write' : 'read-only'}`, '']) },
  { pattern: 'show ip dhcp pool', run: (s) => dhcpPools(s.d) },
  { pattern: 'show ip nat statistics', run: (s) => natStats(s.net, s.id) },
]);
export const SVC_PRIV = R([
  { pattern: 'clear ip dhcp binding <WORD>', run: (s, [w]) => { if (w !== '*') return s.invalidAt(w); s.d.dhcp.bindings = []; return []; } },
  { pattern: 'clear logging', run: (s) => ({ lines: [], ask: { prompt: 'Clear logging buffer [confirm]', answer: (a) => { if (!/^n/i.test(a.trim())) s.d.logs = []; return []; } } }) },
]);
function dhcpPools(d) {
  const out = [];
  for (const p of Object.values(d.dhcp.pools)) {
    if (!p.network) { out.push('', `Pool ${p.name} :`, ' Utilization mark (high/low)    : 100 / 0', ' Subnet size (first/next)       : 0 / 0', ' Total addresses                : 0', ' Leased addresses               : 0', ' Pending event                  : none', ' 0 subnet is currently in the pool'); continue; }
    const size = 2 ** (32 - maskToPrefix(p.mask)) - 2;
    const leased = d.dhcp.bindings.filter((b) => b.pool === p.name).length;
    const base = ipToInt(networkOf(p.network, p.mask)) >>> 0;
    const ipOf = (n) => [n >>> 24, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
    const isExcl = (n) => d.dhcp.excluded.some(([a, b]) => n >= (ipToInt(a) >>> 0) && n <= (ipToInt(b || a) >>> 0));
    let excl = 0, next = null;
    for (let n = base + 1; n <= base + size; n++) {
      if (isExcl(n)) { excl++; continue; }
      if (next === null && !d.dhcp.bindings.some((b) => b.ip === ipOf(n))) next = n;
    }
    out.push('', `Pool ${p.name} :`, ' Utilization mark (high/low)    : 100 / 0', ' Subnet size (first/next)       : 0 / 0', ` Total addresses                : ${size}`, ` Leased addresses               : ${leased}`, ` Excluded addresses             : ${excl}`, ' Pending event                  : none',
      ' 1 subnet is currently in the pool :', ' Current index        IP address range                    Leased/Excluded/Total', `  ${pad(next === null ? '0.0.0.0' : ipOf(next), 21)}${pad(`${ipOf(base + 1)}     - ${ipOf(base + size)}`, 36)}${leased} / ${excl} / ${size}`);
  }
  return out;
}
function natStats(net, id) {
  const d = net.devices[id];
  const N = d.nat;
  const tr = N.translations || [];
  const statics = (N.statics || []).length;
  const dyn = tr.filter((t) => t.type !== 'static').length;
  const ins = Object.entries(d.interfaces).filter(([, i]) => i.nat === 'inside').map(([n]) => n).sort(ifSort);
  const outs = Object.entries(d.interfaces).filter(([, i]) => i.nat === 'outside').map(([n]) => n).sort(ifSort);
  const out = [`Total active translations: ${statics + dyn} (${statics} static, ${dyn} dynamic; ${tr.filter((t) => t.type === 'pat').length} extended)`, `Peak translations: ${statics + dyn}`, 'Outside interfaces:', `  ${outs.join(', ') || ''}`, 'Inside interfaces: ', `  ${ins.join(', ') || ''}`,
    `Hits: ${N.natStats?.hits || 0}  Misses: ${N.natStats?.misses || 0}`, `CEF Translated packets: ${N.natStats?.hits || 0}, CEF Punted packets: 0`, 'Expired translations: 0', 'Dynamic mappings:', '-- Inside Source'];
  N.rules.forEach((r, k) => {
    if (r.ifc) out.push(`[Id: ${k + 1}] access-list ${r.acl} interface ${r.ifc} refcount ${tr.filter((t) => t.type === 'pat' && !t.pool).length}`);
    else {
      const p = N.pools?.[r.pool];
      const total = p ? ((ipToInt(p.end) >>> 0) - (ipToInt(p.start) >>> 0) + 1) : 0;
      const alloc = Object.values(N.natBindings || {}).filter((g) => p && (ipToInt(g) >>> 0) >= (ipToInt(p.start) >>> 0) && (ipToInt(g) >>> 0) <= (ipToInt(p.end) >>> 0)).length + (r.overload ? 1 : 0);
      out.push(`[Id: ${k + 1}] access-list ${r.acl} pool ${r.pool}${r.overload ? ' overload' : ''} refcount ${alloc}`, ` pool ${r.pool}: netmask ${p?.mask || '?'}`, `\tstart ${p?.start} end ${p?.end}`, `\ttype generic, total addresses ${total}, allocated ${alloc} (${total ? Math.round((alloc / total) * 100) : 0}%), misses ${N.natStats?.misses || 0}`);
    }
  });
  out.push('', 'Total doors: 0', 'Appl doors: 0', 'Normal doors: 0', 'Queued Packets: 0');
  return out;
}

// ------------------------------------------------------------------ running-config
export function svcConfigLines(d) {
  const out = [];
  if (d.logging?.buffered) out.push('logging buffered 8192');
  return out;
}
export function svcTailLines(d) {
  const out = [];
  const N = d.nat || {};
  for (const [n, p] of Object.entries(N.pools || {})) out.push(`ip nat pool ${n} ${p.start} ${p.end} netmask ${p.mask}`);
  for (const r of N.rules || []) if (r.pool) out.push(`ip nat inside source list ${r.acl} pool ${r.pool}${r.overload ? ' overload' : ''}`);
  for (const st of N.statics || []) out.push(`ip nat inside source static ${st.local} ${st.global}`);
  const L = d.logging;
  if (L) { if (L.trap && L.trap !== 'informational') out.push(`logging trap ${L.trap}`); for (const h of L.hosts || []) out.push(`logging host ${h}`); }
  const S = d.snmp;
  if (S) {
    for (const [c, m] of Object.entries(S.communities || {})) out.push(`snmp-server community ${c} ${m.toUpperCase()}`);
    if (S.location) out.push(`snmp-server location ${S.location}`);
    if (S.contact) out.push(`snmp-server contact ${S.contact}`);
    if (S.traps) out.push('snmp-server enable traps');
    for (const h of S.hosts || []) out.push(`snmp-server host ${h.ip} version 2c ${h.community}`);
  }
  if (d.ntp) { if (d.ntp.master) out.push(`ntp master ${d.ntp.master}`); for (const ip of d.ntp.servers || []) out.push(`ntp server ${ip}`); }
  return out;
}
export function svcIfLines(n, i) {
  const out = [];
  if (i.hsrpVersion === 2) out.push(' standby version 2');
  for (const g of i.fhrp || []) {
    const kw = g.proto === 'hsrp' ? 'standby' : g.proto;
    if (g.vip) out.push(` ${kw} ${g.group} ip ${g.vip}`);
    if (g.priority !== 100) out.push(` ${kw} ${g.group} priority ${g.priority}`);
    if (g.proto === 'hsrp' && g.preempt) out.push(` ${kw} ${g.group} preempt`);
    if (g.proto !== 'hsrp' && g.preempt === false) out.push(` no ${kw} ${g.group} preempt`);
    if (g.proto === 'glbp' && g.lb && g.lb !== 'round-robin') out.push(` glbp ${g.group} load-balancing ${g.lb}`);
  }
  return out;
}

// ------------------------------------------------------------------ syslog state changes
export function fhrpSnapshot(net, id) {
  const s = {};
  for (const x of Object.values(fhrpState(net))) if (x.dev === id) s[`${x.ifc}|${x.proto}|${x.group}`] = x.state;
  return s;
}
export function fhrpLogs(before, after) {
  const out = [];
  for (const [k, st] of Object.entries(after)) {
    const prev = before[k] || 'Init';
    if (prev === st) continue;
    const [ifc, proto, g] = k.split('|');
    const tag = proto === 'hsrp' ? '%HSRP-5-STATECHANGE' : proto === 'vrrp' ? '%VRRP-6-STATECHANGE' : '%GLBP-6-STATECHANGE';
    out.push({ text: `${tag}: ${shortIf(ifc)} Grp ${g} state ${prev === 'Init' && st !== 'Init' ? 'Speak' : prev} -> ${st}`, delay: 700, log: true });
  }
  return out;
}
export { clockLine, logEvent, isIp, ifStatus };
